import { useState, useEffect, useCallback } from 'react';
import axios from '../../../../configApi/axiosConfig';
import { APIURL } from '../../../../configApi/apiConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Helper: normaliza la respuesta paginada o directa de la API
// ─────────────────────────────────────────────────────────────────────────────
const extractFirstItem = (responseData) => {
    if (Array.isArray(responseData?.data) && responseData.data.length > 0)
        return responseData.data[0];
    if (responseData?.data && typeof responseData.data === 'object')
        return responseData.data;
    if (Array.isArray(responseData) && responseData.length > 0)
        return responseData[0];
    return responseData;
};

const extractArray = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    return [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────
export const useDetalleCobranzaData = (data, isOpen) => {
    const [loading, setLoading]               = useState(false);
    const [selectResultado, setSelectResultado] = useState([]);
    const [direccionData, setDireccionData]   = useState(null);
    const [datosCobranza, setDatosCobranza]   = useState(null);
    const [historialAPI, setHistorialAPI]     = useState([]);
    const [refreshKey, setRefreshKey]         = useState(0);
    const [articulosFactura, setArticulosFactura] = useState([]);
    const [amortizacionStats, setAmortizacionStats] = useState({
        pagadas: 0, impagas: 0, pendientes: 0,
        plazo: 0, totalAbono: 0, diasMora: 0, saldoImpagas: 0, fetched: false
    });

    // ── Carga de dirección / detalles del cliente ──────────────────────────
    const fetchDireccion = useCallback(async (solicitudWeb) => {
        try {
            const response = await axios.get(APIURL.Cbo_GestorDeCobranzasOperativodet(solicitudWeb));
            setDireccionData(extractFirstItem(response.data));
            setSelectResultado(response.data);
        } catch (error) {
            console.error('Error fetching dirección:', error);
        }
    }, []);

    // ── Carga del historial de cobranza ────────────────────────────────────
    const fetchHistorial = useCallback(async (idCompra, solicitudWeb) => {
        try {
            const response = await axios.get(
                APIURL.getDetalleGestoresCobranzasDetalleOperativoWeb(idCompra, solicitudWeb)
            );
            setDatosCobranza(response.data);
            setHistorialAPI(extractArray(response.data));
        } catch (error) {
            console.error('Error fetching historial cobranza:', error);
            setHistorialAPI([]);
        }
    }, []);

    // ── Carga de artículos de la factura ──────────────────────────────────
    const fetchArticulos = useCallback(async (idCompra) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                APIURL.getViewDetalleFacturaOperacion(),
                { idCompra },
                { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
            );
            const items = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.data?.data) ? response.data.data : [];

            // FIX: Primero intentar mostrar solo artículos marcados como Principal=1
            // Si no hay ninguno con Principal, mostrar todos excepto los 100% descuento (promocionales)
            const principales = items.filter(a => Number(a.Principal) === 1);
            const candidatos = principales.length > 0
                ? principales
                : items.filter(a => parseFloat(a.DescuentoPorc || 0) < 100 && parseFloat(a.SubTotal || 0) > 0);

            // Ordenar por SubTotal desc (artículo más caro primero)
            const sorted = [...candidatos].sort((a, b) => parseFloat(b.SubTotal || 0) - parseFloat(a.SubTotal || 0));
            setArticulosFactura(sorted);
        } catch (error) {
            console.error('Error fetching artículos factura:', error);
        }
    }, []);

    // ── Carga de estadísticas de amortización (solo una vez por sesión) ────
    const fetchAmortizacion = useCallback(async (idCompra) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
            const payload = { idCompra };

            const [amResponse] = await Promise.all([
                axios.post(APIURL.getViewTablaAmortizacion(), payload, { headers }),
            ]);

            const productos = amResponse.data || [];
            let pagadas = 0, impagas = 0, pendientes = 0, totalAbono = 0, saldoImpagas = 0;

            productos.forEach((p) => {
                const estado = String(p.Estado);
                if (estado === '2' || estado === 'PAGADO') {
                    pagadas++;
                } else if ((estado === '1' || estado === 'PENDIENTE') && p.Retraso > 0) {
                    impagas++;
                    saldoImpagas += Number(p.Saldo || 0);
                } else if (estado === '1' || estado === 'PENDIENTE') {
                    pendientes++;
                }
                if (p.Abono) totalAbono += Number(p.Abono);
            });

            // FIX: DiasMora se calcula directo desde el Retraso de la tabla de amortización
            // (MAX retraso de cuotas pendientes con vencimiento pasado)
            // Esto coincide exactamente con la columna RETRASO que muestra la tabla.
            const diasMora = productos.reduce((max, p) => {
                const r = Number(p.Retraso || 0);
                return (r > 0 && r > max) ? r : max;
            }, 0);

            setAmortizacionStats({
                pagadas,
                impagas,
                pendientes,
                plazo: productos.length,
                totalAbono,
                saldoImpagas,
                diasMora,
                fetched: true
            });
        } catch (error) {
            console.error('Error fetching amortización:', error);
        }
    }, []);

    // ── Effect principal: se ejecuta al abrir o al pedir un refetch ────────
    useEffect(() => {
        if (!isOpen || !data?.idCompra) return;

        const load = async () => {
            setLoading(true);
            try {
                const promises = [];

                if (data.sCre_SolicitudWeb) {
                    promises.push(fetchDireccion(data.sCre_SolicitudWeb));
                    promises.push(fetchHistorial(data.idCompra, data.sCre_SolicitudWeb));
                    promises.push(fetchArticulos(data.idCompra));
                } else {
                    setHistorialAPI([]);
                }

                // Amortización solo se carga una vez (fetched = false)
                if (!amortizacionStats.fetched) {
                    promises.push(fetchAmortizacion(data.idCompra));
                }

                await Promise.all(promises);
            } finally {
                setLoading(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, data, refreshKey]);
    // Nota: amortizacionStats.fetched se excluye a propósito para evitar
    // re-renders infinitos; fetchAmortizacion ya maneja su propio estado.

    const refetch = useCallback(() => setRefreshKey((prev) => prev + 1), []);

    return {
        loading,
        selectResultado,
        direccionData,
        datosCobranza,
        historialAPI,
        amortizacionStats,
        articulosFactura,
        refetch
    };
};
